import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { toast } from 'react-toastify';
import isEmail from 'validator/lib/isEmail';
import { authApi } from '../../../assets/api/services/authApi';
import {
  getAvailableVerificationChannels,
  TELEGRAM_CONTACT_CHANNEL,
  VERIFICATION_CHANNEL_MAP,
} from '../../../assets/constants/verificationChannels';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { getApiDetail } from '../../../assets/services/loginFlow';
import {
  formatDateOfBirthInput,
  getDateOfBirthApiValue,
  hasDateOfBirthInputValue,
  isDateOfBirthValid,
  normalizeDateOfBirthApiValue,
} from '../../../assets/utils/dateOfBirth';
import {
  triggerImpactHaptic,
  triggerNotificationHaptic,
} from '../../../assets/utils/hapticFeedback';
import {
  formatConfirmedPhone,
  formatPhoneInput,
  getPhoneApiValue,
  handlePhoneInputKeyDown,
  isPhoneValid,
} from '../../../assets/utils/phone';
import { resolveCurrentUserResponse } from '../../../assets/utils/currentUserResponse';
import BottomActionButton from '../../shared/BottomActionButton/BottomActionButton';
import BottomSheet from '../../shared/BottomSheet/BottomSheet';
import './PersonalInfoEditSheet.css';

const CODE_LENGTH = 6;
const REQUEST_COOLDOWN_MS = 60 * 1000;
const CODE_ERROR_HIGHLIGHT_DURATION = 1200;
const CODE_ERROR_SHAKE_ANIMATION = { x: [0, -8, 8, -6, 6, -3, 3, 0] };
const CODE_ERROR_SHAKE_TRANSITION = { duration: 0.42, ease: 'easeInOut' };
const STEP = {
  VALUE: 'value',
  CODE: 'code',
  PHONE_METHOD: 'phone_method',
};
const MAX_TRIES_EXCEEDED_DETAIL = 'Max tries exceeded';
const PHONE_VERIFICATION_CHANNELS = getAvailableVerificationChannels({ phone: true });

const FIELD_CONFIG = {
  name: {
    apiKey: 'name',
    inputType: 'text',
    autoComplete: 'name',
    maxLength: 256,
  },
  dob: {
    apiKey: 'dob',
    inputType: 'text',
    autoComplete: 'bday',
    keyboard: 'numeric',
    maxLength: 10,
    placeholder: 'DD.MM.YYYY',
  },
  email: {
    inputType: 'email',
    autoComplete: 'email',
    keyboard: 'email',
  },
  phone: {
    inputType: 'tel',
    autoComplete: 'tel',
    keyboard: 'tel',
  },
};

function stringifyApiErrorValue(value) {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value.map(stringifyApiErrorValue).filter(Boolean).join('\n');
  }
  if (typeof value === 'object') {
    return value.msg || value.message || value.detail || null;
  }
  return String(value);
}

function getApiErrorMessage(error, fallback) {
  return (
    stringifyApiErrorValue(error?.detail)
    || stringifyApiErrorValue(error?.payload?.detail)
    || stringifyApiErrorValue(error?.payload)
    || stringifyApiErrorValue(error?.message)
    || fallback
  );
}

function normalizeUtcValue(value) {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) return '';
  return /(?:z|[+-]\d{2}:?\d{2})$/i.test(normalizedValue)
    ? normalizedValue
    : `${normalizedValue}Z`;
}

function parseUtcMs(value) {
  const parsedMs = Date.parse(normalizeUtcValue(value));
  return Number.isFinite(parsedMs) ? parsedMs : 0;
}

function getRequestCooldownEndMs(generatedUtc) {
  const generatedMs = parseUtcMs(generatedUtc);
  return generatedMs ? generatedMs + REQUEST_COOLDOWN_MS : 0;
}

function getRequestCooldownRemainingSeconds(generatedUtc, nowMs = Date.now()) {
  const endMs = getRequestCooldownEndMs(generatedUtc);
  return endMs ? Math.max(Math.ceil((endMs - nowMs) / 1000), 0) : 0;
}

function formatRequestCooldown(remainingSeconds, common) {
  const seconds = Math.max(Math.ceil(Number(remainingSeconds) || 0), 0);
  return seconds === 1 ? `1 ${common.second}` : `${seconds} ${common.seconds}`;
}

function useRequestCooldown(generatedUtc) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const cooldownEndMs = useMemo(() => getRequestCooldownEndMs(generatedUtc), [generatedUtc]);
  const remainingSeconds = getRequestCooldownRemainingSeconds(generatedUtc, nowMs);

  useEffect(() => {
    setNowMs(Date.now());
    if (!cooldownEndMs || cooldownEndMs <= Date.now()) return undefined;

    const intervalId = window.setInterval(() => {
      const nextNowMs = Date.now();
      setNowMs(nextNowMs);
      if (nextNowMs >= cooldownEndMs) {
        window.clearInterval(intervalId);
      }
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [cooldownEndMs]);

  return {
    isCooldownActive: remainingSeconds > 0,
    remainingSeconds,
  };
}

function getGeneratedUtcForField(user, field) {
  if (field === 'email') {
    return user?.reg_data?.new_email?.generated_utc || '';
  }

  if (field === 'phone') {
    return user?.reg_data?.new_phone?.generated_utc || '';
  }

  return '';
}

function getGeneratedUtcFromResponse(response, field) {
  return getGeneratedUtcForField(response, field);
}

function patchGeneratedUtc(user, field, generatedUtc) {
  if (!user || !generatedUtc) return user;
  const key = field === 'email' ? 'new_email' : 'new_phone';

  return {
    ...user,
    reg_data: {
      ...(user.reg_data || {}),
      [key]: {
        ...(user.reg_data?.[key] || {}),
        generated_utc: generatedUtc,
      },
    },
  };
}

function clearGeneratedUtc(user, field) {
  if (!user) return user;
  const key = field === 'email' ? 'new_email' : 'new_phone';

  return {
    ...user,
    reg_data: {
      ...(user.reg_data || {}),
      [key]: {
        ...(user.reg_data?.[key] || {}),
        generated_utc: '',
      },
    },
  };
}

function patchPersonalData(user, key, value) {
  if (!user) return user;
  const nextValue = value || null;

  return {
    ...user,
    personal_data: {
      ...(user.personal_data || {}),
      [key]: nextValue,
    },
    ...(key === 'name' ? { name: nextValue } : {}),
  };
}

function patchVerifiedContact(user, field, value) {
  const withClearedTimer = clearGeneratedUtc(user, field);

  if (!withClearedTimer) return withClearedTimer;

  if (field === 'email') {
    return {
      ...withClearedTimer,
      email: value || null,
    };
  }

  return {
    ...withClearedTimer,
    phone: value || null,
    reg_data: {
      ...(withClearedTimer.reg_data || {}),
      new_phone: withClearedTimer.reg_data?.new_phone,
      stages: {
        ...(withClearedTimer.reg_data?.stages || {}),
        is_phone_verified: true,
      },
    },
  };
}

function focusInput(input) {
  if (!input) return false;
  try {
    input.focus({ preventScroll: true });
  } catch {
    input.focus();
  }

  try {
    const valueLength = String(input.value || '').length;
    input.setSelectionRange?.(valueLength, valueLength);
  } catch {
    // Some input types do not support selection ranges.
  }

  return typeof document === 'undefined' ? true : document.activeElement === input;
}

function VerificationCodeInput({
  ariaLabel,
  groupAriaLabel,
  disabled,
  errorKey,
  hasError,
  onChange,
  onComplete,
  readOnly,
  value,
}) {
  const nativeInputRef = useRef(null);
  const [isErrorActive, setIsErrorActive] = useState(false);
  const [hasNativeFocus, setHasNativeFocus] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const code = useMemo(
    () => Array.from({ length: CODE_LENGTH }, (_, index) => value[index] ?? ''),
    [value]
  );
  const activeIndex = Math.min(value.length, CODE_LENGTH - 1);

  const focusNativeInput = useCallback(() => {
    if (disabled) return false;
    return focusInput(nativeInputRef.current);
  }, [disabled]);

  useEffect(() => {
    if (!hasError) {
      setIsErrorActive(false);
      return undefined;
    }

    triggerNotificationHaptic('error');
    setIsErrorActive(true);
    const timeoutId = window.setTimeout(() => {
      setIsErrorActive(false);
    }, CODE_ERROR_HIGHLIGHT_DURATION);

    return () => window.clearTimeout(timeoutId);
  }, [errorKey, hasError]);

  useEffect(() => {
    if (value.length === CODE_LENGTH && !disabled && !readOnly) {
      onComplete(value);
    }
  }, [disabled, onComplete, readOnly, value]);

  useLayoutEffect(() => {
    if (!value) {
      focusNativeInput();
    }
  }, [focusNativeInput, value]);

  useEffect(() => {
    if (disabled || value) return undefined;

    const timeoutIds = [];
    let animationFrameId = null;
    focusNativeInput();

    if (typeof window.requestAnimationFrame === 'function') {
      animationFrameId = window.requestAnimationFrame(focusNativeInput);
    }

    [40, 120, 280].forEach((delay) => {
      timeoutIds.push(window.setTimeout(focusNativeInput, delay));
    });

    return () => {
      if (animationFrameId !== null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(animationFrameId);
      }
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [disabled, focusNativeInput, value]);

  return (
    <motion.div
      className={`personal-info-edit__code ${isErrorActive ? 'personal-info-edit__code--error' : ''}`}
      animate={isErrorActive && !shouldReduceMotion ? CODE_ERROR_SHAKE_ANIMATION : { x: 0 }}
      transition={CODE_ERROR_SHAKE_TRANSITION}
    >
      <div
        className="personal-info-edit__code-row"
        role="group"
        aria-label={groupAriaLabel}
        onPointerDown={(event) => {
          if (disabled) return;
          event.preventDefault();
          focusNativeInput();
        }}
      >
        <input
          ref={nativeInputRef}
          className="personal-info-edit__code-input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          enterKeyHint="done"
          maxLength={CODE_LENGTH}
          value={value}
          disabled={disabled}
          readOnly={readOnly}
          aria-label={ariaLabel}
          autoFocus
          onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
          onFocus={() => setHasNativeFocus(true)}
          onBlur={() => setHasNativeFocus(false)}
        />
        {code.map((digit, index) => (
          <span
            key={index}
            className={[
              'personal-info-edit__code-cell-wrap',
              hasNativeFocus && !disabled && index === activeIndex
                ? 'personal-info-edit__code-cell-wrap--active'
                : null,
            ].filter(Boolean).join(' ')}
            aria-hidden="true"
          >
            <span
              className={[
                'personal-info-edit__code-cell',
                hasNativeFocus && !disabled && !readOnly && index === activeIndex && !digit
                  ? 'personal-info-edit__code-cell--cursor'
                  : null,
              ].filter(Boolean).join(' ')}
            >
              {digit}
            </span>
          </span>
        ))}
      </div>
    </motion.div>
  );
}

function getPhoneChannelLabel(channel, channels) {
  const entry = Object.values(VERIFICATION_CHANNEL_MAP)
    .find((config) => config.preferredChannel === channel || config.channel === channel);
  return channels[entry?.channel]?.label || entry?.label || '';
}

function isSamePhoneChannel(channel, channelConfig) {
  const normalizedChannel = String(channel || '').trim();
  if (!normalizedChannel || !channelConfig) return false;

  return [
    channelConfig.channel,
    channelConfig.preferredChannel,
  ].filter(Boolean).includes(normalizedChannel);
}

function PersonalInfoEditSheet({
  authService = authApi,
  field,
  initialValue = '',
  onClose,
}) {
  const { currentUser, setCurrentUser } = useContext(CurrentUserContext);
  const { channels, common, errors, pin, profile, t } = useContext(TranslationContext);
  const { personal } = profile;
  const config = FIELD_CONFIG[field];
  const inputRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const isCodeCheckingRef = useRef(false);
  const [value, setValue] = useState(() => {
    const nextInitialValue = initialValue === personal.notAdded ? '' : initialValue;
    if (field === 'dob') return formatDateOfBirthInput(nextInitialValue);
    return field === 'phone' ? formatConfirmedPhone(nextInitialValue) : nextInitialValue;
  });
  const [step, setStep] = useState(STEP.VALUE);
  const [code, setCode] = useState('');
  const [codeErrorPulse, setCodeErrorPulse] = useState(0);
  const [isCodeInvalidHighlighted, setIsCodeInvalidHighlighted] = useState(false);
  const [isCodeLocked, setIsCodeLocked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedUtc, setGeneratedUtc] = useState(() => getGeneratedUtcForField(currentUser, field));
  const [usedPhoneChannel, setUsedPhoneChannel] = useState('');
  const cooldown = useRequestCooldown(generatedUtc);
  const cooldownText = formatRequestCooldown(cooldown.remainingSeconds, common);
  const normalizedValue = field === 'phone'
    ? getPhoneApiValue(value)
    : field === 'dob'
      ? getDateOfBirthApiValue(value)
      : String(value || '').trim();
  const normalizedInitialValue = field === 'phone'
    ? getPhoneApiValue(initialValue)
    : field === 'dob'
      ? normalizeDateOfBirthApiValue(initialValue)
      : String(initialValue === personal.notAdded ? '' : initialValue || '').trim();
  const isVerificationField = field === 'email' || field === 'phone';
  const isValueValid = field === 'phone'
    ? isPhoneValid(value)
    : field === 'email'
      ? isEmail(normalizedValue)
      : field === 'dob'
        ? !hasDateOfBirthInputValue(value) || isDateOfBirthValid(value)
        : !isVerificationField || normalizedValue.length > 0;
  const canSubmitValue = normalizedValue !== normalizedInitialValue
    && isValueValid
    && !isSubmitting
    && !cooldown.isCooldownActive;
  const alternativePhoneChannels = field === 'phone'
    ? PHONE_VERIFICATION_CHANNELS.filter((channel) => (
      !isSamePhoneChannel(usedPhoneChannel, VERIFICATION_CHANNEL_MAP[channel])
    ))
    : [];

  useEffect(() => {
    setGeneratedUtc(getGeneratedUtcForField(currentUser, field));
  }, [currentUser, field]);

  useEffect(() => {
    if (step !== STEP.VALUE) return undefined;
    const frame = window.requestAnimationFrame(() => {
      focusInput(inputRef.current);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [step]);

  const commitGeneratedUtc = useCallback((nextGeneratedUtc) => {
    if (!nextGeneratedUtc) return;
    setGeneratedUtc(nextGeneratedUtc);
    setCurrentUser((prevUser) => patchGeneratedUtc(prevUser, field, nextGeneratedUtc));
  }, [field, setCurrentUser]);

  const markCodeInvalid = useCallback(() => {
    setCode('');
    setCodeErrorPulse((currentPulse) => currentPulse + 1);
    setIsCodeInvalidHighlighted(true);
    window.setTimeout(() => {
      setIsCodeInvalidHighlighted(false);
    }, CODE_ERROR_HIGHLIGHT_DURATION);
  }, []);

  const handlePersonalDataSubmit = useCallback(async () => {
    if (!config || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      await authService.editPersonalData({
        setData: {
          [config.apiKey]: normalizedValue || null,
        },
      });

      setCurrentUser((prevUser) => patchPersonalData(prevUser, config.apiKey, normalizedValue || null));

      toast.success(personal.updated);
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error, personal.updateFailed));
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    authService,
    config,
    normalizedValue,
    onClose,
    personal,
    setCurrentUser,
  ]);

  const requestCode = useCallback(async (preferredChannel) => {
    if (!isVerificationField || isSubmittingRef.current || cooldown.isCooldownActive) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      if (field === 'email') {
        const response = await authService.requestEmailChange({ email: normalizedValue });
        const nextGeneratedUtc = getGeneratedUtcFromResponse(response, field) || new Date().toISOString();
        commitGeneratedUtc(nextGeneratedUtc);
      } else {
        const response = await authService.requestPhoneCode({
          phone: normalizedValue,
          preferredChannel,
        });
        setUsedPhoneChannel(response?.channel || preferredChannel || '');
        commitGeneratedUtc(new Date().toISOString());
      }

      setCode('');
      setIsCodeLocked(false);
      setIsCodeInvalidHighlighted(false);
      setStep(STEP.CODE);
    } catch (error) {
      const nextGeneratedUtc = getGeneratedUtcFromResponse(error?.payload, field)
        || getGeneratedUtcFromResponse(error, field);

      if (nextGeneratedUtc) {
        commitGeneratedUtc(nextGeneratedUtc);
      }

      toast.error(getApiErrorMessage(error, errors.failedSendCode));
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    authService,
    commitGeneratedUtc,
    cooldown.isCooldownActive,
    errors.failedSendCode,
    field,
    isVerificationField,
    normalizedValue,
  ]);

  const submitValue = useCallback(() => {
    if (!canSubmitValue) return;

    if (isVerificationField) {
      requestCode(field === 'phone' ? TELEGRAM_CONTACT_CHANNEL : undefined);
      return;
    }

    handlePersonalDataSubmit();
  }, [
    canSubmitValue,
    field,
    handlePersonalDataSubmit,
    isVerificationField,
    requestCode,
  ]);

  const handleValueSubmit = useCallback((event) => {
    event.preventDefault();
    submitValue();
  }, [submitValue]);

  const handleConfirmCode = useCallback(async (nextCode) => {
    const normalizedCode = String(nextCode || '').replace(/\D/g, '').slice(0, CODE_LENGTH);
    let didConfirmCode = false;

    if (normalizedCode.length !== CODE_LENGTH || isCodeCheckingRef.current || isCodeLocked) {
      return;
    }

    isCodeCheckingRef.current = true;
    setIsSubmitting(true);

    try {
      const nextCurrentUser = field === 'email'
        ? await authService.confirmEmailChange({ code: normalizedCode })
        : await authService.validatePhoneCode({ code: normalizedCode });

      setCurrentUser((prevUser) => resolveCurrentUserResponse(
        nextCurrentUser,
        patchVerifiedContact(prevUser, field, normalizedValue)
      ));
      setCode('');
      didConfirmCode = true;
      toast.success(field === 'email' ? personal.emailUpdated : personal.phoneUpdated);
      onClose();
    } catch (error) {
      const isMaxTriesExceeded = getApiDetail(error) === MAX_TRIES_EXCEEDED_DETAIL;
      toast.error(getApiErrorMessage(
        error,
        isMaxTriesExceeded ? pin.tooManyCodeAttempts : pin.invalidCode
      ));
      markCodeInvalid();

      if (isMaxTriesExceeded) {
        setIsCodeLocked(true);
      }
    } finally {
      if (!didConfirmCode) {
        isCodeCheckingRef.current = false;
        setIsSubmitting(false);
      }
    }
  }, [
    authService,
    field,
    isCodeLocked,
    markCodeInvalid,
    onClose,
    normalizedValue,
    personal,
    pin,
    setCurrentUser,
  ]);

  if (!config) return null;

  const localizedFieldConfig = {
    ...config,
    title: {
      name: personal.editFullName,
      dob: personal.editDob,
      email: personal.editEmail,
      phone: personal.editPhone,
    }[field],
    label: {
      name: personal.fullName,
      dob: personal.dateOfBirth,
      email: personal.email,
      phone: personal.phone,
    }[field],
  };

  const renderValueStep = () => (
    <form className="personal-info-edit__form" onSubmit={handleValueSubmit}>
      <label className="personal-info-edit__field">
        <span className="personal-info-edit__label">{localizedFieldConfig.label}</span>
        <input
          ref={inputRef}
          className="personal-info-edit__input"
          type={config.inputType}
          value={value}
          autoComplete={config.autoComplete}
          inputMode={config.keyboard}
          enterKeyHint="done"
          maxLength={config.maxLength}
          placeholder={localizedFieldConfig.placeholder}
          onChange={(event) => {
            setValue(field === 'phone'
              ? formatPhoneInput(event.target.value)
              : field === 'dob'
                ? formatDateOfBirthInput(event.target.value)
                : event.target.value);
          }}
          onKeyDown={(event) => {
            if (field !== 'phone') return;
            handlePhoneInputKeyDown(event, setValue);
          }}
        />
      </label>
      {cooldown.isCooldownActive && isVerificationField && (
        <p className="personal-info-edit__hint">
          {t('pin.requestAnotherCodeIn', { time: cooldownText })}
        </p>
      )}
      <BottomActionButton
        className="personal-info-edit__action"
        type="button"
        disabled={!canSubmitValue}
        onClick={submitValue}
      >
        {isSubmitting
          ? common.sending
          : isVerificationField && cooldown.isCooldownActive
            ? t('pin.sendCodeIn', { time: cooldownText })
            : isVerificationField
              ? pin.sendCode
              : common.save}
      </BottomActionButton>
    </form>
  );

  const renderPhoneMethodStep = () => (
    <div className="personal-info-edit__method-step">
      <button
        type="button"
        className="personal-info-edit__back-link"
        disabled={isSubmitting}
        onClick={() => {
          triggerImpactHaptic('light');
          setStep(STEP.CODE);
        }}
      >
        {common.back}
      </button>
      <p className="personal-info-edit__subtitle">{pin.chooseCodeChannel}</p>
      <div className="personal-info-edit__channels">
        {alternativePhoneChannels.map((channel) => {
          const channelConfig = VERIFICATION_CHANNEL_MAP[channel];
          const channelCopy = channels[channel] || channelConfig;
          const Icon = channelConfig.Icon;

          return (
            <button
              key={channel}
              type="button"
              className="personal-info-edit__channel"
              disabled={isSubmitting || cooldown.isCooldownActive}
              onClick={() => requestCode(channelConfig.preferredChannel || channelConfig.channel)}
            >
              <span className="personal-info-edit__channel-icon" aria-hidden="true">
                <Icon />
              </span>
              <span className="personal-info-edit__channel-text">
                <span className="personal-info-edit__channel-label">{channelCopy.label}</span>
                <span className="personal-info-edit__channel-description">
                  {channelCopy.description}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      {cooldown.isCooldownActive && (
        <p className="personal-info-edit__hint">
          {t('pin.requestAnotherCodeIn', { time: cooldownText })}
        </p>
      )}
    </div>
  );

  const targetLabel = field === 'email'
    ? normalizedValue
    : `+${normalizedValue}`;
  const codeSubtitle = field === 'phone' && usedPhoneChannel
    ? t('pin.codeSentVia', {
      channel: getPhoneChannelLabel(usedPhoneChannel, channels),
      target: targetLabel,
    })
    : t('pin.codeSent', { target: targetLabel });

  const renderCodeStep = () => (
    <div className="personal-info-edit__code-step">
      <p className="personal-info-edit__subtitle">{codeSubtitle}</p>
      <VerificationCodeInput
        ariaLabel={pin.verificationCode}
        groupAriaLabel={t('pin.codeFieldsAria', { label: pin.verificationCode })}
        value={code}
        disabled={isCodeLocked}
        readOnly={isSubmitting}
        hasError={isCodeInvalidHighlighted}
        errorKey={codeErrorPulse}
        onChange={(nextCode) => {
          setIsCodeInvalidHighlighted(false);
          setCode(nextCode);
        }}
        onComplete={handleConfirmCode}
      />
      {isCodeLocked && (
        <p className="personal-info-edit__hint">
          {pin.tooManyCodeAttempts}
        </p>
      )}
      <div className="personal-info-edit__code-actions">
        <button
          type="button"
          className="personal-info-edit__link"
          disabled={isSubmitting || cooldown.isCooldownActive}
          onClick={() => requestCode(
            field === 'phone' ? TELEGRAM_CONTACT_CHANNEL : undefined
          )}
        >
          {isSubmitting
            ? common.sending
            : cooldown.isCooldownActive
              ? t('pin.resendIn', { time: cooldownText })
              : pin.resendCode}
        </button>
        {field === 'phone' && alternativePhoneChannels.length > 0 && (
          <button
            type="button"
            className="personal-info-edit__link"
            disabled={isSubmitting}
            onClick={() => setStep(STEP.PHONE_METHOD)}
          >
            {personal.changeMethod}
          </button>
        )}
      </div>
    </div>
  );

  const sheetTitle = step === STEP.VALUE
    ? localizedFieldConfig.title
    : step === STEP.PHONE_METHOD
      ? personal.changeMethod
      : pin.enterCode;

  const renderCurrentStep = () => {
    if (step === STEP.VALUE) return renderValueStep();
    if (step === STEP.PHONE_METHOD) return renderPhoneMethodStep();
    return renderCodeStep();
  };

  return (
    <BottomSheet
      className="personal-info-edit"
      panelClassName="personal-info-edit__panel"
      title={sheetTitle}
      titleId="personal-info-edit-title"
      onClose={onClose}
    >
      {renderCurrentStep()}
    </BottomSheet>
  );
}

export default PersonalInfoEditSheet;
